/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useMemo } from 'react';
import { useUnpaidOrdersInRange, PayoutLike } from './useUnpaidOrdersInRange.ts';
import { calculateOrderTotalEarned } from '../utils/calculations.ts';

export type { PayoutLike };

interface UseEarnedSincePayoutResult {
  earnedAmount: number | null;
  earnedLoading: boolean;
  unpaidOrders: any[];
}

// Amount earned (net, same calculation as the sales grid) from every
// order placed in the collection since its last payout that hasn't been
// included in a payout yet.
//
// Scoped by lastOrderNumberPaid - the highest order number any payout for
// this collection has ever covered - rather than a date cutoff. Order
// numbers are plain integers with no timezone or string-parsing
// ambiguity, unlike a payout's `date` field (free-text, historically
// stored as non-ISO "MM-DD-YYYY" - exactly what caused the Chrome-vs-
// Safari dashboard mismatch: WebKit silently failed to parse it while
// Chrome parsed it leniently, collapsing the cutoff to 1970 on Safari/the
// app and scanning full history there instead of just what's unpaid).
//
// A collection with no prior payout (or whose payouts predate this field
// and haven't been backfilled yet - see backfill-payout-collection-handles.js)
// passes null and gets no cutoff, scanning full history same as before -
// slower, but never incorrect.
//
// See useRecentUnpaidOrders for the separate fast path the dashboard
// charts use instead, which only ever needs a fixed recent window
// regardless of payout history.
export function useEarnedSincePayout(
  selectedHandle: string | undefined,
  payouts: PayoutLike[],
  payoutsLoading: boolean,
  lastOrderNumberPaid: number | null
): UseEarnedSincePayoutResult {
  // Only used as the request shape when lastOrderNumberPaid is null (no
  // scoping available) - useUnpaidOrdersInRange ignores this entirely
  // whenever minOrderNumber is given. Computed once on mount rather than
  // fresh on every render, so it doesn't look like a "new" range and
  // re-trigger the fetch below for no reason.
  const fullHistoryRange = useMemo(() => ({ start: new Date(2000, 0, 1), end: new Date() }), []);

  const { orders, loading, error } = useUnpaidOrdersInRange(
    selectedHandle,
    payouts,
    payoutsLoading,
    fullHistoryRange,
    lastOrderNumberPaid
  );

  const earnedAmount = useMemo(() => {
    if (!selectedHandle || error) return null;
    return orders.reduce((sum, order) => sum + calculateOrderTotalEarned(order), 0);
  }, [orders, error, selectedHandle]);

  return { earnedAmount, earnedLoading: loading, unpaidOrders: orders };
}
